import { Button } from "@msuite/ui/button";
import { Icon } from "@msuite/ui/icons";
import { useTranslation } from "@msuite/ui/localization";
import { Modal, type ModalProps } from "@msuite/ui/modal";
import { DataTable } from "@msuite/ui/table";
import { useMemo } from "react";
import { useNavigate } from "react-router";
import { FILE_INBOX_PATH } from "@/sites/data/file-inbox/path";
import { createColumns } from "./table-config";
import { useFileBatches } from "./use-file-batches";

/** Props Interface */
interface DropzoneBatchModalProps extends ModalProps {
	/** The drops to report on — the same set the pill is counting. */
	batchIds: string[];
}

/**
 * What became of everything in this drop, filed and unfiled together — and the way to settle what
 * was not.
 *
 * **The filed half has no other account anywhere.** The inbox is a worklist and shows only what is
 * outstanding; the explorer shows a file once it is somewhere, but nobody knows to look. So a
 * document that was read, matched to a project and filed correctly is, without this, something the
 * person who dropped it is asked to take on trust from a number in a pill. That is the gap the
 * report closes, and it is why the table lists every row rather than only the ones needing work.
 *
 * The unfiled rows carry the inbox's own menu, so the drop is finished where it is reported on: the
 * person reading this is the one who dropped the files and the only one who knows where they belong,
 * and sending them to a second page to say so loses most of them. The inbox stays the worklist for
 * everything nobody settled here, and the two lists refresh together.
 */
export function DropzoneBatchModal({ isOpen, onClose, batchIds }: DropzoneBatchModalProps) {
	/** Context */
	const { t, language } = useTranslation();

	/** Hooks */
	const navigate = useNavigate();

	/** Query */
	const { files, isLoading, refetch, queryKey } = useFileBatches(batchIds, isOpen);

	/** Functions */
	function handleOnOpenInbox() {
		navigate(FILE_INBOX_PATH);
		onClose();
	}

	/** Computed */
	const unplacedCount = files.filter(
		(file) => file.state === "unplaced" || file.state === "failed",
	).length;
	// This modal outlives a route change, so the offer import would otherwise leave the report
	// standing on top of the editor it navigated to. `language` is a dependency although the
	// callback does not read it: the column headers are resolved through `localized(...)` when the
	// factory runs, so a language switch has to rebuild them.
	const columns = useMemo(() => createColumns({ onNavigate: onClose }), [onClose, language]);

	/** Render */
	return (
		<Modal
			isOpen={isOpen}
			onClose={onClose}
			title={t("files.batch_overview")}
			width="80vw"
		>
			<Modal.Body>
				<Modal.TableWrapper>
					<DataTable
						data={files}
						columns={columns}
						isLoading={isLoading}
						refetch={refetch}
						queryKey={queryKey}
						menu={null}
						persistenceKey="dropzone-batch-table"
					/>
				</Modal.TableWrapper>
			</Modal.Body>
			<Modal.Footer>
				{/* Offered only when something is actually waiting there. The inbox is where the rest
				    of the organisation's unfiled files are dealt with, and a way into it from a report
				    that found nothing outstanding would send the reader to an empty page. */}
				{unplacedCount > 0 ? (
					<Button
						icon={<Icon name="Inbox" />}
						onClick={handleOnOpenInbox}
					>
						{t("file_upload.open_inbox")}
					</Button>
				) : (
					<div />
				)}
				<Button
					variant="primary"
					onClick={onClose}
				>
					{t("general.close")}
				</Button>
			</Modal.Footer>
		</Modal>
	);
}
